import { Injectable, OnDestroy } from '@angular/core';
import { AuthService } from '@auth0/auth0-angular';
import { Subscription } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { LocalNotifications } from '@capacitor/local-notifications';

/**
 * Topic de FCM al que se suscriben todos los dispositivos móviles.
 * n8n publica en este topic (en paralelo al envío a Gotify) para que la
 * notificación llegue a Android e iOS aunque la app esté cerrada.
 *
 * Es un broadcast a todos los dispositivos, igual que el stream de Gotify hoy.
 * Para notificar a un usuario concreto en el futuro, usar un topic por usuario
 * (p. ej. `user_<id>`) y suscribir aquí con ese nombre.
 */
const ALERTS_TOPIC = 'alerts';

/**
 * Gestiona el registro de notificaciones PUSH reales (FCM en Android, APNs en iOS)
 * mediante @capacitor-firebase/messaging.
 *
 * A diferencia de GotifyService (que usa un WebSocket y solo funciona con la app
 * en primer plano), este servicio entrega notificaciones aunque la app esté en
 * segundo plano o cerrada, porque las despierta el sistema operativo.
 *
 * Lifecycle:
 *  - Se inicializa cuando Auth0 reporta isAuthenticated = true.
 *  - Pide permiso de notificaciones, registra el dispositivo en FCM/APNs y se
 *    suscribe al topic `alerts`.
 *  - En primer plano muestra la notificación con LocalNotifications (iOS no la
 *    muestra automáticamente cuando la app está activa).
 *  - En segundo plano / cerrada, el SO la muestra solo (payload de tipo notification).
 *
 * Solo se activa en plataformas nativas: en web, Gotify cubre las notificaciones de PC.
 */
@Injectable({ providedIn: 'root' })
export class PushService implements OnDestroy {
  private readonly authSub: Subscription;
  private initialized = false;
  private listenersRegistered = false;
  private notifIdSeq = 1;

  constructor(private readonly auth: AuthService) {
    this.authSub = this.auth.isAuthenticated$.subscribe(isAuthenticated => {
      if (isAuthenticated) {
        this.init().catch(err => console.error('[Push] Error de inicialización:', err));
      }
    });
  }

  // ─── Inicialización ────────────────────────────────────────────────────────

  private async init(): Promise<void> {
    // Push real solo en Android / iOS. En web, Gotify cubre las notificaciones.
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    if (this.initialized) {
      // Reasegura la suscripción al topic en logins posteriores.
      await this.subscribeToAlerts();
      return;
    }
    this.initialized = true;

    await this.registerListeners();

    const granted = await this.requestPermission();
    if (!granted) {
      console.warn('[Push] Permiso de notificaciones no concedido.');
      return;
    }

    // Registra el dispositivo en FCM/APNs (genera y entrega el token al backend de Firebase).
    try {
      await FirebaseMessaging.getToken();
    } catch (err) {
      console.error('[Push] No se pudo obtener el token de FCM:', err);
      return;
    }

    await this.subscribeToAlerts();
  }

  private async requestPermission(): Promise<boolean> {
    try {
      const { receive } = await FirebaseMessaging.requestPermissions();
      return receive === 'granted';
    } catch (err) {
      console.error('[Push] Error al solicitar permisos:', err);
      return false;
    }
  }

  private async subscribeToAlerts(): Promise<void> {
    try {
      await FirebaseMessaging.subscribeToTopic({ topic: ALERTS_TOPIC });
      console.info(`[Push] Suscrito al topic "${ALERTS_TOPIC}".`);
    } catch (err) {
      console.error(`[Push] No se pudo suscribir al topic "${ALERTS_TOPIC}":`, err);
    }
  }

  // ─── Listeners ───────────────────────────────────────────────────────────

  private async registerListeners(): Promise<void> {
    if (this.listenersRegistered) {
      return;
    }
    this.listenersRegistered = true;

    // Notificación recibida con la app en PRIMER PLANO.
    // (En segundo plano / cerrada el SO la muestra automáticamente.)
    await FirebaseMessaging.addListener('notificationReceived', event => {
      const n = event.notification;
      this.showForegroundNotification(n?.title, n?.body).catch(err =>
        console.error('[Push] Error mostrando notificación en primer plano:', err)
      );
    });

    // El usuario tocó la notificación (app abierta desde la notificación).
    await FirebaseMessaging.addListener('notificationActionPerformed', event => {
      // Punto de extensión: enrutar según event.notification.data si se incluye
      // un campo de navegación en el payload enviado desde n8n.
      console.info('[Push] Notificación pulsada:', event.notification?.data);
    });
  }

  // ─── Notificación en primer plano ────────────────────────────────────────

  private async showForegroundNotification(title?: string, body?: string): Promise<void> {
    if (!title && !body) {
      return;
    }
    try {
      let perm = await LocalNotifications.checkPermissions();
      if (perm.display !== 'granted') {
        perm = await LocalNotifications.requestPermissions();
        if (perm.display !== 'granted') {
          return;
        }
      }

      // LocalNotifications requiere un id int32 positivo.
      const notifId = (this.notifIdSeq++ % 2_147_483_646) + 1;

      await LocalNotifications.schedule({
        notifications: [
          {
            id: notifId,
            title: title ?? 'Synapseguard',
            body: body ?? '',
            // Coincide con el drawable en android/app/src/main/res/
            smallIcon: 'ic_launcher_foreground',
            iconColor: '#00b4d8',
            actionTypeId: 'PUSH_ALERT',
          },
        ],
      });
    } catch (err) {
      console.error('[Push] Error al programar la notificación local:', err);
    }
  }

  // ─── Cleanup ───────────────────────────────────────────────────────────────

  ngOnDestroy(): void {
    this.authSub.unsubscribe();
  }
}
